import { PARCEL_OVERRIDE_STORAGE_KEY, type ParcelRemotes } from '../types';
import type { OverrideResult, OverrideSourceType, PerScopeOverrideResult } from './types';
import { isDevMode } from '../utils/is-dev-mode';
import {
  PARCEL_OVERRIDE_URL_PARAM,
  PARCEL_OVERRIDE_ENV_KEY,
  PARCEL_ORCHESTRATOR_GLOBAL,
  OVERRIDE_SOURCE_PRIORITY,
} from './constants';

function isParcelRemotes(value: unknown): value is ParcelRemotes {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.entries(value).every(([k, v]) => typeof k === 'string' && typeof v === 'string')
  );
}

/**
 * Per-scope override resolution. Each scope is independently resolved
 * from the highest-priority source that defines it.
 *
 * Priority per scope: URL > localStorage > orchestrator > env
 */
export function getActiveOverridesPerScope(): PerScopeOverrideResult {
  if (!isDevMode()) return { overrides: {}, sources: {} };
  if (typeof window === 'undefined') return { overrides: {}, sources: {} };

  const overrides: ParcelRemotes = {};
  const sources: Record<string, OverrideSourceType> = {};

  // Layer 1 (lowest priority): env
  const envJson = typeof process !== 'undefined' && process.env?.[PARCEL_OVERRIDE_ENV_KEY];
  if (envJson) {
    try {
      const parsed: unknown = JSON.parse(envJson);
      if (isParcelRemotes(parsed)) {
        for (const [scope, url] of Object.entries(parsed)) {
          overrides[scope] = url;
          sources[scope] = 'env';
        }
      } else if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn(
          '[parcels] Override env config has invalid shape (expected Record<string, string>)',
        );
      }
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn(
          '[parcels] Failed to parse override env config:',
          err instanceof Error ? err.message : err,
        );
      }
    }
  }

  // Layer 2: orchestrator (window global set by bridge composable)
  const orchestratorOverrides = (window as unknown as Record<string, unknown>)[
    PARCEL_ORCHESTRATOR_GLOBAL
  ];
  if (isParcelRemotes(orchestratorOverrides)) {
    for (const [scope, url] of Object.entries(orchestratorOverrides)) {
      overrides[scope] = url;
      sources[scope] = 'orchestrator';
    }
  }

  // Layer 3: localStorage
  try {
    const stored = localStorage.getItem(PARCEL_OVERRIDE_STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isParcelRemotes(parsed)) {
        for (const [scope, url] of Object.entries(parsed)) {
          overrides[scope] = url;
          sources[scope] = 'localStorage';
        }
      } else if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn(
          '[parcels] localStorage overrides have invalid shape (expected Record<string, string>)',
        );
      }
    }
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.warn(
        '[parcels] Failed to parse localStorage overrides:',
        err instanceof Error ? err.message : err,
      );
    }
  }

  // Layer 4 (highest priority): URL params
  const params = new URLSearchParams(window.location.search);
  const overrideParam = params.get(PARCEL_OVERRIDE_URL_PARAM);
  if (overrideParam) {
    const urlOverrides = parseOverrideParam(overrideParam);
    for (const [scope, url] of Object.entries(urlOverrides)) {
      overrides[scope] = url;
      sources[scope] = 'url';
    }
  }

  return { overrides, sources };
}

/**
 * Get active overrides in the legacy winner-takes-all format.
 * Kept for backward compatibility — delegates to per-scope resolution
 * and picks the dominant source for the `source` field.
 */
export function getActiveOverrides(): OverrideResult {
  if (!isDevMode()) return { source: null, overrides: {} };
  const { overrides, sources } = getActiveOverridesPerScope();
  if (Object.keys(overrides).length === 0) return { source: null, overrides: {} };

  // Pick the highest-priority source present across all scopes
  const presentSources = new Set(Object.values(sources));
  const dominantSource = OVERRIDE_SOURCE_PRIORITY.find((s) => presentSources.has(s)) ?? null;

  return { source: dominantSource, overrides };
}

export function getLocalStorageOverrides(): ParcelRemotes {
  if (!isDevMode()) return {};
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(PARCEL_OVERRIDE_STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (isParcelRemotes(parsed)) {
        return parsed;
      } else if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        console.warn(
          '[parcels] localStorage overrides have invalid shape (expected Record<string, string>)',
        );
      }
    }
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.warn(
        '[parcels] Failed to parse localStorage overrides:',
        err instanceof Error ? err.message : err,
      );
    }
  }

  return {};
}

export function setLocalStorageOverride(scope: string, url: string): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;

  const current = getLocalStorageOverrides();
  current[scope] = url;
  localStorage.setItem(PARCEL_OVERRIDE_STORAGE_KEY, JSON.stringify(current));
}

export function removeLocalStorageOverride(scope: string): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;

  const current = getLocalStorageOverrides();
  delete current[scope];

  if (Object.keys(current).length === 0) {
    localStorage.removeItem(PARCEL_OVERRIDE_STORAGE_KEY);
  } else {
    localStorage.setItem(PARCEL_OVERRIDE_STORAGE_KEY, JSON.stringify(current));
  }
}

export function clearAllLocalStorageOverrides(): void {
  if (!isDevMode()) return;
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PARCEL_OVERRIDE_STORAGE_KEY);
}

export function generateOverrideUrlParam(overrides: ParcelRemotes): string {
  const entries = Object.entries(overrides);
  if (entries.length === 0) return '';

  const encoded = entries.map(([scope, url]) => `${scope}:${url}`).join(';');
  return `${PARCEL_OVERRIDE_URL_PARAM}=${encodeURIComponent(encoded)}`;
}

export function generateShareableUrl(overrides?: ParcelRemotes): string {
  if (typeof window === 'undefined') return '';

  const effectiveOverrides = overrides ?? getActiveOverrides().overrides;
  if (Object.keys(effectiveOverrides).length === 0) {
    return window.location.href;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(PARCEL_OVERRIDE_URL_PARAM);

  const entries = Object.entries(effectiveOverrides);
  if (entries.length > 0) {
    const overrideValue = entries.map(([scope, entryUrl]) => `${scope}:${entryUrl}`).join(';');
    url.searchParams.set(PARCEL_OVERRIDE_URL_PARAM, overrideValue);
  }

  return url.toString();
}

function parseOverrideParam(param: string): ParcelRemotes {
  const overrides: ParcelRemotes = {};

  for (const entry of param.split(';')) {
    const trimmed = entry.trim();
    if (!trimmed) continue;

    const [scope, url] = trimmed.split(/:(.+)/);
    if (scope && url) {
      overrides[scope] = url;
    } else if (isDevMode()) {
      console.warn(`[parcels] Ignoring malformed override entry: "${trimmed}"`);
    }
  }

  return overrides;
}
